import {
  isSupportedLocale,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@brightdrop/localization-core';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LanguageIcon from '@mui/icons-material/Language';
import {
  Box,
  FormControl,
  type FormControlProps,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { LOCALE_OPTION_LABELS, LOCALE_OPTION_ORDER } from './constants';

const TRANSLATION_KEYS = {
  label: 'common:language',
} as const;

const COMPACT_SELECT_SX: SxProps<Theme> = {
  color: 'text.secondary',
  typography: 'body_medium',
  // The theme fills every SvgIcon with the primary text colour, so `color`
  // alone would leave both icons darker than the label.
  '& .MuiSvgIcon-root': {
    fill: 'currentColor',
    fontSize: '1.125rem',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    paddingBlock: 0.5,
  },
  // With no underline and a transparent focus background, this outline is the
  // only cue keyboard users get that the picker has focus. It is drawn on the
  // root so it wraps the chevron, which sits outside the focused element.
  borderRadius: 1,
  '&:has(.MuiSelect-select:focus-visible)': {
    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
  '& .MuiSelect-icon': {
    color: 'inherit',
    marginLeft: 0.5,
  },
};

const menuPosition = (locale: SupportedLocale): number => {
  const position = LOCALE_OPTION_ORDER.indexOf(locale);
  return position === -1 ? LOCALE_OPTION_ORDER.length : position;
};

export type LocaleSwitcherProps = {
  /** The selected locale, or an empty string while none is set. */
  value: SupportedLocale | '';
  onChange: (locale: SupportedLocale) => void;
  /** Test id of the select; also derives the label id and the option ids (`${testId}-option-${locale}`). */
  testId: string;
  /** Locales offered in the dropdown; defaults to every locale the core ships. */
  options?: readonly SupportedLocale[];
  disabled?: boolean;
  size?: FormControlProps['size'];
  className?: string;
  /**
   * `field` is a labelled form field. `compact` is a borderless globe-and-language
   * trigger for page chrome, labelled for assistive technology only.
   */
  variant?: 'field' | 'compact';
};

/**
 * The language dropdown shared by the profile form, the login screen and Quick
 * Pay. The caller decides what a choice does and may narrow which locales are
 * offered; by default every locale the localization core ships is listed.
 * Options are always listed in the product's menu order, whatever order the
 * caller passes them in.
 * @param value - The currently selected locale
 * @param onChange - Called with the newly selected locale
 * @param testId - Test id of the select
 * @param options - Locales offered in the dropdown
 * @param disabled - Locks the dropdown
 * @param size - Field height
 * @param className - Class applied to the form control
 * @param variant - Labelled form field or compact trigger
 */
const LocaleSwitcher = ({
  value,
  onChange,
  testId,
  options = SUPPORTED_LOCALES,
  disabled = false,
  size = 'medium',
  className,
  variant = 'field',
}: LocaleSwitcherProps): JSX.Element => {
  // Subscribe to i18n so imperative `translateText(...)` updates after locale changes.
  useTranslation(['common']);

  const labelId = `${testId}-label`;
  const label = translateText(TRANSLATION_KEYS.label);
  // MUI warns on, and renders blank for, a value outside the offered options.
  const selectedValue = value && options.includes(value) ? value : '';
  const orderedOptions = [...options].sort(
    (a, b) => menuPosition(a) - menuPosition(b)
  );

  const handleChange = (event: SelectChangeEvent<SupportedLocale | ''>) => {
    if (isSupportedLocale(event.target.value)) {
      onChange(event.target.value);
    }
  };

  const menuItems = orderedOptions.map((locale) => (
    <MenuItem
      key={locale}
      value={locale}
      lang={locale}
      data-testid={`${testId}-option-${locale}`}
    >
      {LOCALE_OPTION_LABELS[locale]}
    </MenuItem>
  ));

  if (variant === 'compact') {
    return (
      <FormControl
        variant="standard"
        size={size}
        disabled={disabled}
        className={className}
      >
        <Select
          variant="standard"
          disableUnderline
          displayEmpty
          value={selectedValue}
          onChange={handleChange}
          IconComponent={KeyboardArrowDownIcon}
          inputProps={{ 'aria-label': label }}
          renderValue={(locale) => (
            <Box display="flex" alignItems="center" gap={1}>
              <LanguageIcon />
              {locale && (
                <span lang={locale}>{LOCALE_OPTION_LABELS[locale]}</span>
              )}
            </Box>
          )}
          data-testid={testId}
          sx={COMPACT_SELECT_SX}
        >
          {menuItems}
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth
      variant="filled"
      size={size}
      disabled={disabled}
      className={className}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        variant="filled"
        label={label}
        value={selectedValue}
        onChange={handleChange}
        IconComponent={KeyboardArrowDownIcon}
        data-testid={testId}
      >
        {menuItems}
      </Select>
    </FormControl>
  );
};

export default LocaleSwitcher;
